/**
 * Écrans d'intégration : console API, numéros de test, exemples de code.
 *
 * Ils vivaient dans un espace « bac à sable » qui mélangeait deux questions —
 * qui vous êtes, et où vous travaillez. Ils appartiennent désormais à l'espace
 * développeur. L'environnement, lui, est celui du déploiement : test et
 * production sont deux plateformes distinctes, il n'y a rien à basculer.
 *
 * La console exécute de véritables requêtes contre l'API de bac à sable — ce
 * n'est pas une simulation d'interface. Le cycle de vie affiché est celui que
 * le serveur produit réellement, accusés de réception compris.
 */
import { useEffect, useRef, useState } from 'react';
import { api, type Message } from '../../api/client';
import { Bandeau, Carte, EtatVide, MessageErreur, Puce } from '../../components/Ui';
import { formaterHeure } from '../../lib/format';
import { useEstProduction } from '../../lib/plateforme';

// --- Console API ------------------------------------------------------------

const requeteInitiale = (destinataire: string, expediteur: string) =>
  `{
  "to": "${destinataire}",
  "from": "${expediteur}",
  "body": "Votre code de validation est 4821."
}`;

/** Statuts non terminaux : on continue d'interroger tant qu'on y est. */
const EN_MOUVEMENT = new Set(['queued', 'submitted', 'sent']);

/**
 * `numero` vient de l'écran « Numéros de test » quand on clique sur Essayer :
 * la requête est réamorcée avec ce destinataire.
 */
export function ConsoleApi({ numero }: { numero?: string | null }) {
  // L'environnement n'est plus une propriété : c'est celui du déploiement.
  // Cette console émettait toujours en `test` du temps où elle vivait dans un
  // espace « bac à sable ». Maintenant que test et production sont deux
  // plateformes séparées, elle émet simplement sur celle où elle tourne.
  const estProduction = useEstProduction();

  const [expediteur, setExpediteur] = useState('DEVSANDBOX');
  const [requete, setRequete] = useState(() =>
    requeteInitiale(numero ?? '+241 66 00 00 00', 'DEVSANDBOX'),
  );

  // Un expéditeur validé du compte rend l'exemple immédiatement exécutable.
  useEffect(() => {
    api
      .expediteurs()
      .then(({ data }) => {
        const choisi = data.find((e) => e.status === 'approved') ?? data[0];
        if (!choisi) return;
        setExpediteur(choisi.value);
        setRequete((actuelle) =>
          actuelle.includes('"DEVSANDBOX"')
            ? actuelle.replace('"DEVSANDBOX"', `"${choisi.value}"`)
            : actuelle,
        );
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (numero) setRequete(requeteInitiale(numero, expediteur));
  }, [numero]);
  const [reponse, setReponse] = useState<Message | null>(null);
  const [statutHttp, setStatutHttp] = useState<string | null>(null);
  const [duree, setDuree] = useState<number | null>(null);
  const [suivi, setSuivi] = useState<Message | null>(null);
  const [erreur, setErreur] = useState<unknown>(null);
  const [enCours, setEnCours] = useState(false);
  const minuterie = useRef<ReturnType<typeof setInterval> | null>(null);

  const arreterSuivi = () => {
    if (minuterie.current) clearInterval(minuterie.current);
    minuterie.current = null;
  };

  useEffect(() => arreterSuivi, []);

  function reinitialiser() {
    arreterSuivi();
    setReponse(null);
    setSuivi(null);
    setStatutHttp(null);
    setDuree(null);
    setErreur(null);
  }

  async function executer() {
    reinitialiser();
    setEnCours(true);
    const debut = performance.now();

    try {
      const corps = JSON.parse(requete) as {
        to: string;
        from: string;
        body: string;
        category?: string;
      };
      const message = await api.envoyer(corps);
      setDuree(Math.round(performance.now() - debut));
      setStatutHttp(message.status === 'opted_out' ? 'HTTP/1.1 200 OK' : 'HTTP/1.1 202 Accepted');
      setReponse(message);
      setSuivi(message);

      // Le cycle de vie du bac à sable est simulé côté serveur : on interroge
      // jusqu'à l'état terminal, exactement comme le ferait une intégration.
      minuterie.current = setInterval(async () => {
        const majour = await api.message(message.id).catch(() => null);
        if (!majour) return;
        setSuivi(majour);
        if (!EN_MOUVEMENT.has(majour.status)) arreterSuivi();
      }, 1_200);
    } catch (e) {
      setDuree(Math.round(performance.now() - debut));
      if (e instanceof SyntaxError) {
        setErreur(new Error('Le corps de la requête n’est pas un JSON valide.'));
      } else {
        setErreur(e);
      }
    } finally {
      setEnCours(false);
    }
  }

  const corpsReponse = reponse
    ? JSON.stringify(
        {
          id: reponse.id,
          status: reponse.status,
          to: reponse.to,
          from: reponse.from,
          encoding: reponse.encoding,
          segments: reponse.segments,
          cost_xaf: reponse.cost_xaf,
          created_at: reponse.created_at,
        },
        null,
        2,
      )
    : null;

  return (
    <>
      {!estProduction ? (
        <Bandeau titre="Aucun message réel n’est émis ici et votre solde n’est pas débité.">
          Les envois sont simulés de bout en bout : identifiant de message, accusés de réception,
          erreurs opérateur. Le comportement dépend du numéro appelé — voir{' '}
          <strong>Numéros de test</strong>.
        </Bandeau>
      ) : (
        <Bandeau ton="rouge" titre="Production : ces envois partent réellement et sont facturés.">
          Chaque exécution atteint l’opérateur et débite votre solde au segment. Basculez sur{' '}
          <strong>Bac à sable</strong> dans l’en-tête pour mettre au point sans frais.
        </Bandeau>
      )}

      <MessageErreur erreur={erreur} />

      <div className="rangee">
        <div className="large">
          <div className="terminal">
            <header>
              <span className="verbe">POST</span>
              <span className="mono">/v1/messages</span>
              <span
                className="mono"
                style={{
                  marginLeft: 'auto',
                  color: estProduction ? 'var(--rouge)' : undefined,
                }}
              >
                {estProduction ? 'production' : 'test'}
              </span>
            </header>
            <div className="corps">
              <textarea
                rows={9}
                value={requete}
                onChange={(e) => setRequete(e.target.value)}
                spellCheck={false}
              />
              <div
                style={{
                  display: 'flex',
                  gap: 10,
                  marginTop: 12,
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                <button className="accent" onClick={executer} disabled={enCours}>
                  {enCours ? 'Exécution…' : 'Exécuter la requête'}
                </button>
                <button className="sombre" onClick={reinitialiser}>
                  Réinitialiser
                </button>
                {duree !== null && (
                  <span
                    className="mono"
                    style={{
                      marginLeft: 'auto',
                      fontSize: 11.5,
                      color: 'rgba(255,255,255,.5)',
                    }}
                  >
                    {duree} ms
                  </span>
                )}
              </div>

              <div className="sortie">
                {corpsReponse ? (
                  <>
                    <div style={{ color: '#9bd1a5', marginBottom: 6 }}>{statutHttp}</div>
                    {corpsReponse}
                  </>
                ) : (
                  <span style={{ color: 'rgba(255,255,255,.4)' }}>
                    La réponse de l’API s’affichera ici.
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="etroit">
          <Carte titre="Cycle de vie du message">
            {!suivi ? (
              <div className="aide" style={{ marginTop: 0 }}>
                Exécutez une requête pour suivre les accusés de réception tels que votre serveur les
                recevra.
              </div>
            ) : (
              <>
                <ol className="chronologie">
                  {(suivi.events ?? []).map((e, i) => (
                    <li key={`${e.status}-${i}`}>
                      <time className="mono">{formaterHeure(e.occurred_at)}</time>
                      <Puce valeur={e.status} />
                      {e.detail && (
                        <span style={{ color: 'var(--muet)', flexBasis: '100%' }}>{e.detail}</span>
                      )}
                    </li>
                  ))}
                </ol>
                {!EN_MOUVEMENT.has(suivi.status) && (
                  <div className="aide">
                    Cycle terminé — état final <strong>{suivi.status}</strong>. Coût :{' '}
                    {suivi.cost_xaf} FCFA.{' '}
                    {!estProduction
                      ? 'Aucun débit : plateforme de test.'
                      : 'Débité au grand livre à l’acceptation opérateur.'}
                  </div>
                )}
              </>
            )}
          </Carte>
        </div>
      </div>
    </>
  );
}

// --- Numéros de test --------------------------------------------------------

/**
 * Les comportements sont ceux réellement implémentés par le pilote simulé, pas
 * une liste décorative : ils dépendent des deux derniers chiffres du numéro.
 */
const NUMEROS = [
  {
    num: '+241 66 00 00 00',
    comportement: 'Remise normale',
    recu: 'submitted → sent → delivered',
  },
  {
    num: '+241 66 00 00 01',
    comportement: 'Non remis par l’opérateur',
    recu: 'submitted → sent → undelivered · recrédit',
  },
  {
    num: '+241 66 00 00 02',
    comportement: 'Expiré après rétention',
    recu: 'submitted → sent → expired · recrédit',
  },
  {
    num: '+241 66 00 00 03',
    comportement: 'Rejet à la soumission',
    recu: 'rejected · simulated_rejection · jamais débité',
  },
  {
    num: '+241 66 00 00 04',
    comportement: 'Passerelle indisponible',
    recu: 'la tâche est réessayée, le message reste en file',
  },
  {
    num: 'tout autre numéro',
    comportement: 'Remise normale',
    recu: 'submitted → sent → delivered',
  },
];

export function NumerosDeTest({ surEssayer }: { surEssayer: (numero: string) => void }) {
  return (
    <>
      <Bandeau titre="Chaque numéro déclenche un comportement précis, et toujours le même.">
        Un test qui passe aujourd’hui passe demain. Utilisez-les pour vérifier que votre intégration
        traite les échecs aussi rigoureusement que les succès — c’est là que se jouent les litiges
        de facturation.
      </Bandeau>

      <Carte sansCorps>
        <div className="tableau-conteneur">
          <table style={{ minWidth: 680 }}>
            <thead>
              <tr>
                <th>Numéro de test</th>
                <th>Comportement</th>
                <th>Ce que reçoit votre serveur</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {NUMEROS.map((n) => (
                <tr key={n.num}>
                  <td className="mono" style={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>
                    {n.num}
                  </td>
                  <td>{n.comportement}</td>
                  <td className="mono" style={{ color: 'var(--muet)', fontSize: 12 }}>
                    {n.recu}
                  </td>
                  <td className="droite">
                    {n.num.startsWith('+') && (
                      <button className="secondaire petit" onClick={() => surEssayer(n.num)}>
                        Essayer
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Carte>
    </>
  );
}

// --- Exemples de code -------------------------------------------------------

const EXEMPLES: { cle: string; libelle: string; code: string }[] = [
  {
    cle: 'curl',
    libelle: 'cURL',
    code: `curl -X POST https://api.infratex.ga/v1/messages \\
  -H "Authorization: Bearer $INFRATEX_CLE" \\
  -H "Idempotency-Key: $(uuidgen)" \\
  -H "Content-Type: application/json" \\
  -d '{
    "to": "+241 66 00 00 00",
    "from": "MABANQUE",
    "body": "Votre code de validation est 4821.",
    "category": "transactional"
  }'`,
  },
  {
    cle: 'node',
    libelle: 'Node.js',
    code: `import { randomUUID } from 'node:crypto';

const reponse = await fetch('https://api.infratex.ga/v1/messages', {
  method: 'POST',
  headers: {
    'Authorization': \`Bearer \${process.env.INFRATEX_CLE}\`,
    // Sans clé d'idempotence, un retry réseau crée un second message
    // et un second débit. Le réseau gabonais impose des retries.
    'Idempotency-Key': randomUUID(),
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    to: '+241 66 00 00 00',
    from: 'MABANQUE',
    body: 'Votre code de validation est 4821.',
    category: 'transactional',
  }),
});

if (reponse.status === 402) {
  // Solde insuffisant : rien n'a été créé, rien n'a été débité.
  const { error } = await reponse.json();
  throw new Error(error.message);
}

const message = await reponse.json();
console.log(message.id, message.segments, message.cost_xaf);`,
  },
  {
    cle: 'python',
    libelle: 'Python',
    code: `import os, uuid, requests

reponse = requests.post(
    "https://api.infratex.ga/v1/messages",
    headers={
        "Authorization": f"Bearer {os.environ['INFRATEX_CLE']}",
        "Idempotency-Key": str(uuid.uuid4()),
    },
    json={
        "to": "+241 66 00 00 00",
        "from": "MABANQUE",
        "body": "Votre code de validation est 4821.",
        "category": "transactional",
    },
    timeout=10,
)

if reponse.status_code == 402:
    raise RuntimeError(reponse.json()["error"]["message"])

message = reponse.json()
print(message["id"], message["segments"], message["cost_xaf"])`,
  },
  {
    cle: 'php',
    libelle: 'PHP',
    code: `<?php
$ch = curl_init('https://api.infratex.ga/v1/messages');
curl_setopt_array($ch, [
    CURLOPT_POST => true,
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_HTTPHEADER => [
        'Authorization: Bearer ' . getenv('INFRATEX_CLE'),
        'Idempotency-Key: ' . bin2hex(random_bytes(16)),
        'Content-Type: application/json',
    ],
    CURLOPT_POSTFIELDS => json_encode([
        'to' => '+241 66 00 00 00',
        'from' => 'MABANQUE',
        'body' => 'Votre code de validation est 4821.',
        'category' => 'transactional',
    ]),
]);

$reponse = curl_exec($ch);
$statut = curl_getinfo($ch, CURLINFO_RESPONSE_CODE);
curl_close($ch);

if ($statut === 402) {
    throw new RuntimeException(json_decode($reponse, true)['error']['message']);
}`,
  },
  {
    cle: 'webhook',
    libelle: 'Vérifier un webhook',
    code: `import crypto from 'node:crypto';

/**
 * Vérifie X-Infratex-Signature: t=<epoch>,v1=<hex>
 * Deux contrôles, tous deux indispensables :
 *   1. comparaison à temps constant
 *   2. refus d'un horodatage de plus de cinq minutes — sans quoi la
 *      signature ne protège pas du rejeu.
 */
export function signatureValide(corpsBrut, entete, secret) {
  const parties = Object.fromEntries(
    entete.split(',').map((p) => p.split('=')),
  );
  const horodatage = Number(parties.t);
  if (!Number.isFinite(horodatage)) return false;
  if (Math.abs(Date.now() / 1000 - horodatage) > 300) return false;

  const attendue = crypto
    .createHmac('sha256', secret)
    .update(\`\${horodatage}.\${corpsBrut}\`)
    .digest('hex');

  return crypto.timingSafeEqual(
    Buffer.from(attendue, 'hex'),
    Buffer.from(parties.v1 ?? '', 'hex'),
  );
}`,
  },
];

export function Exemples() {
  const [onglet, setOnglet] = useState('curl');
  const exemple = EXEMPLES.find((e) => e.cle === onglet) ?? EXEMPLES[0]!;

  return (
    <>
      <div className="onglets" role="tablist">
        {EXEMPLES.map((e) => (
          <button
            key={e.cle}
            role="tab"
            aria-selected={onglet === e.cle}
            onClick={() => setOnglet(e.cle)}
          >
            {e.libelle}
          </button>
        ))}
      </div>

      <Carte>
        <pre className="code">{exemple.code}</pre>
        <div className="aide">
          Le passage en production consiste à remplacer la clé{' '}
          <span className="mono">ik_test_</span> par une clé <span className="mono">ik_live_</span>.
          Rien d’autre ne change : même URL, même contrat, mêmes codes d’erreur.
        </div>
      </Carte>

      <div style={{ marginTop: 16 }}>
        <Carte titre="Ce qu’il ne faut pas oublier">
          <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 1.9 }}>
            <li>
              <strong>Idempotency-Key sur chaque envoi.</strong> Sans elle, un délai d’attente
              réseau suivi d’un retry crée un second message et un second débit.
            </li>
            <li>
              <strong>Traiter le 402 explicitement.</strong> Solde insuffisant : aucun message n’est
              créé, aucune écriture n’est portée au grand livre.
            </li>
            <li>
              <strong>Répondre vite aux webhooks</strong> — moins de cinq secondes — et traiter en
              différé. Le délai d’attente compte comme un échec.
            </li>
            <li>
              <strong>Ne pas se fier au nombre de caractères pour estimer le coût.</strong> Un seul
              caractère hors alphabet GSM fait basculer tout le message en Unicode et divise la
              capacité par plus de deux. Utilisez{' '}
              <span className="mono">/v1/messages/estimate</span>.
            </li>
          </ul>
        </Carte>
      </div>
    </>
  );
}

export function EtatVideSandbox() {
  return <EtatVide titre="Écran indisponible." />;
}
